<script>
import Breadcrumb from '@/components/global/breadcrumb.vue';
import axios from "axios";
import BulkSms from "@/components/reservation/BulkSms.vue";
import Swal from "sweetalert2";
import { useGradeStore } from '@/stores/useGradeStore.js';

export default {
  name: "chapters",
  inject: {
    courseCtx: { default: null },
  },
  data() {
    return {
      gradeSlug: this.$route.params.gradeSlug,
      courseId: this.$route.params.courseId ?? null,
      dragIndex: null,
      chapters: [],
    };
  },
  components: {
    BulkSms,
    Breadcrumb,
  },
  computed: {
    isCourseScope() { return !!this.courseId; },
    basePath() {
      return this.isCourseScope
          ? `/courses/${this.courseId}/chapters`
          : `/chapter/${this.gradeSlug}`;
    },
    lessonsBasePath() {
      return this.isCourseScope
          ? `/courses/${this.courseId}/lessons`
          : `/lesson/${this.gradeSlug}`;
    },
    breadcrumbs() {
      if (this.isCourseScope) {
        const base = this.courseCtx?.breadcrumbBase ?? [];
        return [...base, { name: 'الأبواب', path: this.basePath }];
      }
      return [
        { name: 'الرئيسية', path: '/' },
        { name: 'الأبواب', path: this.basePath },
      ];
    },
    gradeStore() { return useGradeStore(); },
    resolvedGrade() {
      const slug = this.gradeSlug;
      return this.gradeStore.activeGrades.find(g => g.slug === slug)
          || this.gradeStore.allGrades.find(g => g.slug === slug)
          || null;
    },
    gradeId() {
      return this.resolvedGrade?.id ?? null;
    },
  },
  methods: {
    fetchChapters() {
      const params = this.isCourseScope
          ? { type: 2, course_id: this.courseId }
          : { type: 2, academic_year_id: this.gradeId };
      axios
          .get('/lessons', { params })
          .then((response) => {
            this.chapters = response.data.data;
          })
          .catch(() => {
            this.chapters = [];
          });
    },
    deleteChapter(chapterId) {
      Swal.fire({
        title: "هل تريد الحذف ؟",
        text: "سيتم حذف الباب وجميع دروسه ومحتواه نهائيا",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        cancelButtonText: "رجوع",
        confirmButtonText: "احذف",
      }).then((result) => {
        if (result.isConfirmed) {
          axios
              .delete(`lessons/${chapterId}`)
              .then(({ data }) => {
                if (data.status == true) {
                  Swal.fire("تم الحذف بنجاح", "", "success");
                  this.chapters = this.chapters.filter(c => c.id !== chapterId);
                } else {
                  Swal.fire("", data.message, "error");
                }
              });
        }
      });
    },
    async toggleHide(chapter) {
      const newHide = chapter.hide ? 0 : 1;
      try {
        await axios.post('lessons/hide', {
          lesson_id: chapter.id,
          hide: newHide
        });
        chapter.hide = newHide;
      } catch (e) {
        // handle error (optional)
      }
    },
    onDragStart(index) {
      this.dragIndex = index;
    },
    async onDrop(targetIndex) {
      if (this.dragIndex === null || this.dragIndex === targetIndex) return;
      const moved = this.chapters.splice(this.dragIndex, 1)[0];
      this.chapters.splice(targetIndex, 0, moved);
      await axios.post('lessons/reorder', {
        lesson_id: moved.id,
        order: targetIndex + 1
      });
      this.dragIndex = null;
    },
  },
  async mounted() {
    if (!this.isCourseScope) {
      if (!this.gradeStore.activeGrades.length) await this.gradeStore.fetchActiveGrades();
      if (!this.gradeStore.allGrades.length) await this.gradeStore.fetchAllGrades();
    }
    this.fetchChapters();
  }
}
</script>

<template>
  <Breadcrumb :items="breadcrumbs" />
  <div class="container-fluid my-3">
    <div class="filter my-3">
      <div class="filter-head">
        <h4 class="mb-0">الأبواب</h4>
        <div class="buttons">
          <router-link :to="`${basePath}/add`" class="btn PrimaryButton">إضافة باب</router-link>
        </div>
      </div>
    </div>
    <div class="row reservation-table">
      <div class="col-12">
        <div class="table-responsive">
          <table class="table table-striped">
            <thead>
              <tr>
                <th scope="col">#</th>
                <th scope="col">العنوان</th>
                <th scope="col">الوصف</th>
                <th scope="col">الحالة</th>
                <th scope="col">التحكم</th>
              </tr>
            </thead>
            <tbody>
              <tr
                  v-for="(chapter, index) in chapters"
                  :key="chapter.id"
                  draggable="true"
                  @dragstart="onDragStart(index)"
                  @dragover.prevent
                  @drop="onDrop(index)"
                  style="cursor: move;"
                >
                <td data-label="ID">{{ index + 1 }}</td>
                <td data-label="الاسم">{{ chapter.title }}</td>
                <td data-label="الوصف">{{ chapter.description }}</td>
                <td data-label="الحالة">{{ chapter.hide ? "مخفي" : "ظاهر" }}</td>
                <td data-label="التحكم">
                  <router-link class="btn btn-secondary text-white mx-1"
                               :tooltip="'إضافة درس'"
                               flow="up"
                               :to="`${lessonsBasePath}/add?chapter_id=${chapter.id}`">
                    <i class="pi pi-plus"></i>
                  </router-link>
                  <router-link class="btn btn-primary text-white mx-1"
                               :tooltip="'تعديل'"
                               flow="up"
                               :to="`${basePath}/edit/${chapter.id}`">
                    <i class="pi pi-pencil"></i>
                  </router-link>
                  <button
                      :class="chapter.hide ? 'btn btn-success' : 'btn btn-warning'"
                      @click="toggleHide(chapter)"
                      :tooltip="chapter.hide ? 'إظهار' : 'إخفاء'"
                      flow="up"
                  >
                    <i :class="chapter.hide ? 'pi pi-eye' : 'pi pi-eye-slash'"></i>
                  </button>
                  <button class="btn btn-danger text-white mx-1"
                          type="button"
                          :tooltip="`حذف`"
                          flow="up"
                          @click="deleteChapter(chapter.id)">
                    <i class="pi pi-trash"></i>
                  </button>
                </td>
              </tr>
              <tr v-if="chapters.length === 0">
                <td colspan="5" class="text-center">لا يوجد بيانات</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>

</style>
